import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Car, User, CheckCircle, Download } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { format } from 'date-fns';
import { useToast } from '../hooks/use-toast';
import { bookingAPI, settingsAPI, paymentAPI } from '../api';

const BookingPage = () => {
  const { toast } = useToast();
  const [pricing, setPricing] = useState({ marrickvillePrice: 30, turrellaPrice: 20, gstRate: 0.10 });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [entryDate, setEntryDate] = useState(null);
  const [exitDate, setExitDate] = useState(null);
  const [entryDateOpen, setEntryDateOpen] = useState(false);
  const [exitDateOpen, setExitDateOpen] = useState(false);
  const [carRegistrations, setCarRegistrations] = useState(['']);
  const [numberOfCars, setNumberOfCars] = useState('1');
  const [transferService, setTransferService] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);

  useEffect(() => {
    loadPricing();
  }, []);

  useEffect(() => {
    const numCars = parseInt(numberOfCars);
    const newRegistrations = Array(numCars).fill('').map((_, i) => carRegistrations[i] || '');
    setCarRegistrations(newRegistrations);
  }, [numberOfCars]);

  const loadPricing = async () => {
    try {
      const data = await settingsAPI.getPricing();
      setPricing(data);
    } catch (error) {
      console.error('Error loading pricing:', error);
    }
  };

  const locations = [
    { id: 'marrickville', name: 'Marrickville', address: '100 Marrickville Road, 2204', price: pricing.marrickvillePrice },
    { id: 'turrella', name: 'Turrella', address: '76 Henderson Street, 2205', price: pricing.turrellaPrice }
  ];

  const calculateParkingSubtotal = () => {
    if (!selectedLocation || !entryDate || !exitDate) return 0;
    const days = Math.ceil((exitDate - entryDate) / (1000 * 60 * 60 * 24));
    const parkingCost = days * selectedLocation.price * parseInt(numberOfCars);
    const transferCost = transferService ? (pricing.transferServicePrice || 200) : 0;
    return parkingCost + transferCost;
  };

  const calculateGST = () => {
    return calculateParkingSubtotal() * pricing.gstRate;
  };

  const calculateTotal = () => {
    return calculateParkingSubtotal() + calculateGST();
  };

  const calculateDays = () => {
    if (!entryDate || !exitDate) return 0;
    return Math.ceil((exitDate - entryDate) / (1000 * 60 * 60 * 24));
  };

  const handleCarRegistrationChange = (index, value) => {
    const newRegistrations = [...carRegistrations];
    newRegistrations[index] = value.toUpperCase();
    setCarRegistrations(newRegistrations);
  };

  const handleDownloadPDF = async () => {
    try {
      const blob = await bookingAPI.downloadPDF(createdBooking.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking_confirmation_${createdBooking.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "PDF Downloaded",
        description: "Your booking confirmation has been downloaded.",
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download PDF. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation || !entryDate || !exitDate || !fullName || !email || !phone || !agreedToTerms) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields and agree to terms.",
        variant: "destructive"
      });
      return;
    }

    const hasEmptyRegistration = carRegistrations.some(reg => !reg.trim());
    if (hasEmptyRegistration) {
      toast({
        title: "Missing Car Registration",
        description: "Please enter all car registration numbers.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Format dates as YYYY-MM-DD in local timezone to avoid UTC conversion issues
      const formatDateLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const bookingData = {
        fullName,
        email,
        phone,
        location: selectedLocation.name,
        entryDate: formatDateLocal(entryDate),
        exitDate: formatDateLocal(exitDate),
        carRegistrations,
        numberOfCars: parseInt(numberOfCars),
        transferService,
        transferServicePrice: pricing.transferServicePrice || 200,
        parkingSubtotal: calculateParkingSubtotal(),
        gst: calculateGST(),
        total: calculateTotal()
      };

      const checkoutSession = await paymentAPI.createCheckoutSession(bookingData);
      
      toast({
        title: "Redirecting to Payment",
        description: "You will be redirected to secure payment page...",
      });

      // Check if we're in an iframe - if so, open in new tab or parent window
      if (window.self !== window.top) {
        // We're in an iframe - open Stripe in parent window or new tab
        window.open(checkoutSession.url, '_top');
      } else {
        // Normal redirect
        window.location.href = checkoutSession.url;
      }

    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Payment Error",
        description: error.response?.data?.detail || "Failed to initiate payment. Please try again.",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="min-h-screen py-12 px-4 sm:px-6 lg:px-8"
      style={{ background: 'linear-gradient(135deg, rgb(227, 242, 253) 0%, rgb(179, 229, 252) 50%, rgb(129, 212, 250) 100%)' }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block bg-white px-8 py-6 rounded-2xl shadow-lg mb-6">
            <h1 
              className="text-4xl sm:text-5xl font-bold mb-2"
              style={{ color: '#004280', fontStyle: 'italic' }}
              data-testid="main-heading"
            >
              Cruise Ship Parking Sydney
            </h1>
            <div className="flex flex-col items-center justify-center gap-1">
              <div className="flex items-center gap-2 text-lg">
                <span className="text-[#00897B] font-semibold">●</span>
                <p className="text-gray-700">Secure & Convenient Parking</p>
                <span className="text-[#00897B] font-semibold">●</span>
              </div>
              <p className="text-gray-600 text-sm font-medium">Outdoor Parking Only</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-5">
              {/* Location Selection */}
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-[#004280] flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#00897B]" />
                  Select Parking Location
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {locations.map((location) => (
                    <button
                      key={location.id}
                      type="button"
                      onClick={() => setSelectedLocation(location)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedLocation?.id === location.id
                          ? 'border-[#00897B] bg-green-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-[#00897B] hover:shadow-md'
                      }`}
                      data-testid={`location-${location.id}`}
                    >
                      <h3 className="text-lg font-bold mb-1 text-[#004280]">{location.name}</h3>
                      <p className="text-xs mb-2 text-gray-600">{location.address}</p>
                      <div className="text-2xl font-bold text-[#00897B]">
                        ${location.price}<span className="text-sm font-normal">/day</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Selection */}
              <div className="space-y-3">
                <h2 className="text-xl font-semibold text-[#004280] flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#00897B]" />
                  Select Dates
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600 font-medium">Entry Date</Label>
                    <Popover open={entryDateOpen} onOpenChange={setEntryDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full h-12 justify-start text-left font-normal border-input shadow-sm"
                          data-testid="entry-date-picker"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {entryDate ? format(entryDate, 'dd MMMM yyyy') : 'Select entry date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={entryDate}
                          onSelect={(date) => {
                            setEntryDate(date);
                            setEntryDateOpen(false);
                          }}
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600 font-medium">Exit Date</Label>
                    <Popover open={exitDateOpen} onOpenChange={setExitDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full h-12 justify-start text-left font-normal border-input shadow-sm"
                          data-testid="exit-date-picker"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {exitDate ? format(exitDate, 'dd MMMM yyyy') : 'Select exit date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={exitDate}
                          onSelect={(date) => {
                            setExitDate(date);
                            setExitDateOpen(false);
                          }}
                          disabled={(date) => date < (entryDate || new Date())}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              {/* Car Details */}
              <div className="space-y-3">
                <h2 className="text-xl font-semibold text-[#004280] flex items-center gap-2">
                  <Car className="w-5 h-5 text-[#00897B]" />
                  Car Details
                </h2>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <div className="col-span-2 space-y-2">
                      <Label className="text-sm text-gray-600 font-medium">Car 1 Registration</Label>
                      <Input
                        placeholder="E.G., ABC123"
                        value={carRegistrations[0] || ''}
                        onChange={(e) => handleCarRegistrationChange(0, e.target.value)}
                        className="h-11 uppercase"
                        data-testid="car-registration-0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-gray-600 font-medium">Number of Cars</Label>
                      <Select value={numberOfCars} onValueChange={setNumberOfCars}>
                        <SelectTrigger className="h-11" data-testid="num-cars-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map((num) => (
                            <SelectItem key={num} value={String(num)}>
                              {num}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {carRegistrations.slice(1).map((reg, index) => (
                    <div key={index + 1} className="space-y-2">
                      <Label className="text-sm text-gray-600 font-medium">Car {index + 2} Registration</Label>
                      <Input
                        placeholder="E.G., ABC123"
                        value={reg}
                        onChange={(e) => handleCarRegistrationChange(index + 1, e.target.value)}
                        className="h-11 uppercase"
                        data-testid={`car-registration-${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Transfer Service */}
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-[#00897B] rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="transfer"
                      checked={transferService}
                      onCheckedChange={setTransferService}
                      className="mt-1"
                      data-testid="transfer-checkbox"
                    />
                    <div className="flex-1">
                      <Label 
                        htmlFor="transfer" 
                        className="text-base font-semibold text-[#004280] cursor-pointer block mb-2"
                      >
                        Optional Extra: Private Car Transfer Service
                      </Label>
                      <div className="text-sm space-y-1">
                        <p className="text-gray-700">
                          <strong className="text-[#00897B]">${pricing.transferServicePrice || 200}</strong> - Private Car Transfer to and from Cruise Terminals
                        </p>
                        <p className="text-gray-600 italic">(Only available for up to 5 passengers)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Your Information */}
              <div className="space-y-3">
                <h2 className="text-xl font-semibold text-[#004280] flex items-center gap-2">
                  <User className="w-5 h-5 text-[#00897B]" />
                  Your Information
                </h2>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Full Name</Label>
                    <Input
                      placeholder="John Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="h-12"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Email Address</Label>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12"
                        data-testid="customer-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Phone Number</Label>
                      <Input
                        type="tel"
                        placeholder="0412 345 678"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="h-12"
                        data-testid="customer-phone-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-start space-x-2 pt-2">
                    <Checkbox
                      id="terms"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      data-testid="terms-checkbox"
                    />
                    <Label 
                      htmlFor="terms" 
                      className="text-sm text-gray-600 leading-relaxed cursor-pointer font-medium"
                    >
                      I agree to the parking terms and conditions. I understand that parking is at my own risk.
                    </Label>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full h-14 text-lg font-semibold bg-[#00897B] hover:bg-[#00796B] text-white rounded-lg shadow-lg"
                data-testid="submit-booking-btn"
              >
                <CheckCircle className="mr-2" size={20} />
                {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
              </Button>
            </div>

            {/* Right Column - Booking Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-white to-green-50 rounded-xl p-6 sticky top-8 border-2 border-[#00897B] shadow-lg">
                <h2 className="text-xl font-semibold text-[#004280] mb-4 pb-2 border-b-2 border-[#00897B]">
                  Booking Summary
                </h2>
                {!selectedLocation || !entryDate || !exitDate ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">Select location and dates to see pricing</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-sm">Location</span>
                        <span className="font-semibold text-[#004280]">{selectedLocation.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-sm">Entry Date</span>
                        <span className="font-medium text-gray-800">{format(entryDate, 'dd MMMM yyyy')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-sm">Exit Date</span>
                        <span className="font-medium text-gray-800">{format(exitDate, 'dd MMMM yyyy')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-sm">Duration</span>
                        <span className="font-semibold text-[#004280]">
                          {calculateDays()} {calculateDays() === 1 ? 'day' : 'days'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-sm">Number of Cars</span>
                        <span className="font-semibold text-[#004280]">{numberOfCars}</span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          Parking (${selectedLocation.price} × {calculateDays()} days × {numberOfCars})
                        </span>
                        <span className="font-medium">
                          ${(calculateDays() * selectedLocation.price * parseInt(numberOfCars)).toFixed(2)}
                        </span>
                      </div>
                      {transferService && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Transfer Service</span>
                          <span className="font-medium">${(pricing.transferServicePrice || 200).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium">${calculateParkingSubtotal().toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">GST (10%)</span>
                        <span className="font-medium">${calculateGST().toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="border-t-2 border-[#00897B] pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-[#004280]">Total</span>
                        <span className="text-2xl font-bold text-[#00897B]">${calculateTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-4 text-center">$25 cancellation fee applies</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#00897B] flex items-center gap-2">
              <CheckCircle size={32} className="text-green-600" />
              Booking Confirmed!
            </DialogTitle>
            <DialogDescription>
              Your parking reservation has been successfully confirmed.
            </DialogDescription>
          </DialogHeader>
          {createdBooking && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-[#00897B]">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Booking ID:</strong> {createdBooking.id}
                </p>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Location:</strong> {createdBooking.location}
                </p>
                <p className="text-sm text-gray-700">
                  A confirmation email with all details has been sent to <strong>{createdBooking.email}</strong>
                </p>
              </div>
              
              <Button
                onClick={handleDownloadPDF}
                className="w-full bg-[#00897B] hover:bg-[#00796B]"
                size="lg"
              >
                <Download className="mr-2" size={20} />
                Download Confirmation PDF
              </Button>
              
              <Button
                onClick={() => setShowSuccessDialog(false)}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingPage;
